import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useClerk } from "@clerk/clerk-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/components/ui/field";
import { useAuth } from "@/auth/AuthContext";
import { ApiError } from "@/lib/api";
import { authApi } from "@/lib/auth-api";
import { LogOut, Mail, Phone, Building, Shield, Clock, KeyRound, Loader2, ExternalLink } from "lucide-react";
import { formatDayTimeIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";

const Profile = () => {
  const tz = useTenantTimezone();
  const { user, logout, refreshMe } = useAuth();
  const clerk = useClerk();
  const navigate = useNavigate();

  const initial = useMemo(
    () => ({
      name: user?.name ?? "",
      phone: user?.phone ?? "",
      department: user?.department ?? "",
    }),
    [user?.name, user?.phone, user?.department],
  );

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const dirty =
    form.name.trim() !== initial.name ||
    form.phone.trim() !== initial.phone ||
    form.department.trim() !== initial.department;

  const handleSave = async () => {
    if (!dirty) return;
    if (!form.name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await authApi.updateMe({
        name: form.name.trim(),
        phone: form.phone.trim(),
        department: form.department.trim(),
      });
      await refreshMe();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => setForm(initial);

  // Password change moved to Clerk's UserProfile widget — handles
  // re-authentication, MFA challenges, and session revocation natively.
  const openClerkProfile = () => clerk.openUserProfile();

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden">
        <div className="h-2 bg-gradient-hero" />
        <div className="p-6 flex flex-wrap items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-primary text-2xl font-bold text-white shadow-elegant">
            {user.avatarInitials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold truncate">{user.name}</h2>
              <Badge variant="outline" className="text-xs">{user.role}</Badge>
            </div>
            <div className="mt-1 text-sm text-muted-foreground truncate">
              {user.department || "No department"} · {user.email}
            </div>
          </div>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <LogOut className="mr-1.5 h-4 w-4" />
            Sign out
          </Button>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2 space-y-5">
          <div>
            <h3 className="font-display font-semibold">Account information</h3>
            <p className="text-xs text-muted-foreground">
              Email and role are managed by your administrator.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <Input value={user.email} disabled />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="e.g. +62 812 3456 7890"
              />
            </Field>
            <Field label="Department">
              <Input
                value={form.department}
                onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              />
            </Field>
            <Field label="Role">
              <Input value={user.role} disabled />
            </Field>
            <Field label="Last login">
              <Input value={formatDayTimeIn(user.lastLoginAt, tz)} disabled />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={handleCancel} disabled={!dirty || saving}>
              Cancel
            </Button>
            <Button
              className="bg-gradient-primary"
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>

          <div className="border-t pt-5 space-y-3">
            <div>
              <h4 className="font-semibold flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Security
              </h4>
              <p className="text-xs text-muted-foreground">
                Password, two-factor authentication, OAuth accounts, and active sessions are
                managed in your account settings.
              </p>
            </div>
            <div>
              <Button variant="outline" onClick={openClerkProfile}>
                <ExternalLink className="mr-1.5 h-4 w-4" />
                Manage account & security
              </Button>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-3">Quick info</h3>
            <ul className="space-y-3 text-sm">
              <Info icon={Mail} label={user.email} />
              <Info icon={Phone} label={user.phone || "—"} />
              <Info icon={Building} label={user.department || "—"} />
              <Info icon={Shield} label={user.role} />
              <Info icon={Clock} label={`Last login: ${formatDayTimeIn(user.lastLoginAt, tz)}`} />
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};


const Info = ({ icon: Icon, label }: { icon: React.ComponentType<{ className?: string }>; label: string }) => (
  <li className="flex items-center gap-2.5 text-muted-foreground">
    <Icon className="h-4 w-4 text-primary" />
    <span className="text-foreground truncate">{label}</span>
  </li>
);

export default Profile;
